import { supabase } from '../lib/supabase'
import { MarketEMP, MarketEMPInput } from '../types/database'

export class MarketEMPsService {
  /**
   * Get all EMPs for a specific model
   */
  static async getByModelId(modelId: number): Promise<MarketEMP[]> {
    try {
      const { data, error } = await supabase
        .from('market_emps')
        .select('*')
        .eq('model_id', modelId)
        .eq('is_active', true)
        .order('name')

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get all EMPs for a specific model (including inactive)
   */
  static async getAllByModelId(modelId: number): Promise<MarketEMP[]> {
    try {
      const { data, error } = await supabase
        .from('market_emps')
        .select('*')
        .eq('model_id', modelId)
        .order('name')

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get the active EMP for a specific model (there should only be one)
   */
  static async getActiveByModelId(modelId: number): Promise<MarketEMP | null> {
    try {
      const { data, error } = await supabase
        .from('market_emps')
        .select('*')
        .eq('model_id', modelId)
        .eq('is_active', true)
        .single()

      if (error) {
        // If no active EMP found, return null instead of throwing
        if (error.code === 'PGRST116') {
          return null
        }
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Create a new EMP
   */
  static async create(empData: MarketEMPInput): Promise<MarketEMP> {
    try {
      // Check if there's already an active EMP for this model
      const existingActiveEMP = await this.getActiveByModelId(empData.model_id)

      // If is_active is not explicitly set, determine the default value
      let isActive = empData.is_active
      if (isActive === undefined) {
        // If there's no existing active EMP, make this one active
        // If there is an existing active EMP, make this one inactive
        isActive = existingActiveEMP === null
      }

      const { data, error } = await supabase
        .from('market_emps')
        .insert({
          ...empData,
          is_active: isActive
        })
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Update an existing EMP
   */
  static async update(id: string, empData: Partial<MarketEMPInput>): Promise<MarketEMP> {
    try {
      const { data, error } = await supabase
        .from('market_emps')
        .update(empData)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete an EMP
   */
  static async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('market_emps')
        .delete()
        .eq('id', id)

      if (error) {
        throw error
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Toggle EMP active status - ensures only one EMP is active per model
   */
  static async toggleActive(id: string, isActive: boolean): Promise<MarketEMP> {
    try {
      // If activating an EMP, first deactivate all other EMPs for the same model
      if (isActive) {
        // Get the model_id for this EMP
        const { data: empData, error: empError } = await supabase
          .from('market_emps')
          .select('model_id')
          .eq('id', id)
          .single()

        if (empError) {
          throw empError
        }

        // Deactivate all other EMPs for this model
        const { error: deactivateError } = await supabase
          .from('market_emps')
          .update({ is_active: false })
          .eq('model_id', empData.model_id)
          .neq('id', id)

        if (deactivateError) {
          throw deactivateError
        }
      }

      // Now update the target EMP
      const { data, error } = await supabase
        .from('market_emps')
        .update({ is_active: isActive })
        .eq('id', id)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      throw error
    }
  }
}
